import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, ClipboardCheck, Pause, Play } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { AttendanceModal } from "@/pages/TeacherDashboard";
import ClassStories from "@/components/ClassStories";

// A teacher's dashboard, laid out like the Little Verses teacher home: pick the class and section you are teaching,
// then one card tells you where that class is - not started yet (press "Start from Today"), ready to teach, or paused -
// with attendance and pause right there, and the stories that class has reached below. Everything uses the same
// /teacher/* endpoints as the Classes page (which keeps the full roster, pulse and mark-progress tools).

export default function TeacherHome() {
  const navigate = useNavigate();
  const [scopes, setScopes] = useState(null);
  const [className, setClassName] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showAttendance, setShowAttendance] = useState(false);

  const load = useCallback(async (keep) => {
    try {
      const { data } = await client.get("/teacher/scopes");
      const list = data.scopes || [];
      setScopes(list);
      if (list.length) {
        const wanted = keep && list.find((s) => s.scope_id === keep);
        const pick = wanted || list[0];
        setClassName(pick.class_name || pick.name);
        setScopeId(pick.scope_id);
      }
    } catch (e) {
      setScopes([]);
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const classes = useMemo(() => {
    const map = new Map();
    (scopes || []).forEach((s) => {
      const k = s.class_name || s.name;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    });
    return [...map.entries()].map(([name, sections]) => ({ name, sections }));
  }, [scopes]);

  const group = classes.find((c) => c.name === className) || null;
  const scope = (scopes || []).find((s) => s.scope_id === scopeId) || null;

  const onClass = (name) => {
    setClassName(name);
    const g = classes.find((c) => c.name === name);
    if (g && g.sections.length) setScopeId(g.sections[0].scope_id);
  };

  const act = async (path) => {
    if (!scope) return;
    setBusy(true); setErr("");
    try {
      await client.post(path, { scope_type: scope.scope_type, scope_id: scope.scope_id });
      await load(scope.scope_id);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (scopes === null) return <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 text-hv-slate">Loading…</main>;

  const reached = scope && scope.started ? Number(scope.max_unlocked_seq || 0) : 0;
  const current = scope && scope.started ? scope.current_story_seq : null;
  const sectionLabel = (s) => (s.class_name && s.name !== s.class_name ? s.name : "All students");

  const CARD = "rounded-[2rem] bg-white border border-[#EDE6D2] shadow-sm";
  const LABEL = "text-[11px] font-bold uppercase tracking-wider text-hv-slate";
  const SELECT = "w-full min-w-[200px] appearance-none rounded-2xl bg-[#F4F1E6] ps-4 pe-11 py-3 font-extrabold text-hv-navy "
    + "border-2 border-transparent focus:outline-none focus:border-hv-teal cursor-pointer";
  const OUTLINE = "inline-flex items-center gap-2 rounded-full border-2 border-[#CFE3DC] bg-white px-5 py-2.5 font-bold text-hv-navy "
    + "hover:bg-[#F1F8F5] disabled:opacity-60 transition-colors";

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="teacher-home">
      {scopes.length === 0 ? (
        <section className={`${CARD} p-8 sm:p-10`} data-testid="teacher-home-empty">
          <h1 className="hv-heading text-3xl sm:text-4xl">Not assigned to a class yet</h1>
          <p className="text-lg text-hv-slate mt-2">Ask your principal or admin to assign you to a class.</p>
          {err && <p className="mt-3 text-sm text-hv-coral">{err}</p>}
        </section>
      ) : (
        <>
          <section className={`${CARD} p-5 sm:p-6 flex flex-wrap items-end gap-4`} data-testid="teacher-class-picker">
            <label className="flex flex-col gap-1.5 relative">
              <span className={LABEL}>Class</span>
              <select value={className} onChange={(e) => onClass(e.target.value)} data-testid="home-class-select" className={SELECT}>
                {classes.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <ChevronDown size={18} strokeWidth={2.6} className="pointer-events-none absolute end-4 bottom-3.5 text-hv-navy" />
            </label>
            <label className="flex flex-col gap-1.5 relative">
              <span className={LABEL}>Section</span>
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="home-section-select" className={SELECT}>
                {(group?.sections || []).map((s) => <option key={s.scope_id} value={s.scope_id}>{sectionLabel(s)}</option>)}
              </select>
              <ChevronDown size={18} strokeWidth={2.6} className="pointer-events-none absolute end-4 bottom-3.5 text-hv-navy" />
            </label>
          </section>

          <section className={`mt-5 ${CARD} p-6 sm:p-10`} data-testid="teacher-class-card">
            {scope && !scope.started ? (
              <>
                <h1 className="hv-heading text-3xl sm:text-5xl leading-tight">
                  Start {className}{sectionLabel(scope) !== "All students" ? ` · ${sectionLabel(scope)}` : ""}?
                </h1>
                <p className="text-lg text-hv-slate mt-3 max-w-2xl">
                  Story 1 opens the moment you press this, and every later story follows the normal weekly schedule
                  from today — the same as it works for a family.
                </p>
                <button type="button" onClick={() => act("/teacher/start-release")} disabled={busy} data-testid="home-start-release"
                        className="hv-btn-primary mt-8 inline-flex items-center gap-2 px-8 py-4 text-lg">
                  {busy ? "Starting…" : "Start from Today"} <ArrowRight size={20} strokeWidth={2.6} />
                </button>
              </>
            ) : scope ? (
              <>
                <div className="flex items-start justify-between gap-5 flex-wrap">
                  <div className="min-w-0">
                    <h1 className="hv-heading text-3xl sm:text-5xl leading-tight">
                      {scope.paused ? "This class is paused" : "Today's class is ready"}
                    </h1>
                    <p className="text-lg text-hv-slate mt-3">
                      {scope.paused
                        ? "No new stories will open until you resume it — everyone stays exactly where they are."
                        : `${scope.student_count} student${scope.student_count === 1 ? "" : "s"} in this class — continues right where the class left off.`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button type="button" onClick={() => setShowAttendance(true)} data-testid="home-attendance" className={OUTLINE}>
                      <ClipboardCheck size={18} strokeWidth={2.5} /> Take attendance
                    </button>
                    <button type="button" onClick={() => act(scope.paused ? "/teacher/release-resume" : "/teacher/release-pause")}
                            disabled={busy} data-testid="home-toggle-pause" className={OUTLINE}>
                      {scope.paused ? <Play size={18} strokeWidth={2.5} /> : <Pause size={18} strokeWidth={2.5} />}
                      {scope.paused ? "Resume class" : "Pause class"}
                    </button>
                  </div>
                </div>
                {!scope.paused && current && (
                  <button type="button" onClick={() => navigate(`/story/${current}`)} data-testid="home-start-lesson"
                          className="hv-btn-primary mt-8 inline-flex items-center gap-2 px-8 py-4 text-lg">
                    Start today&apos;s story <ArrowRight size={20} strokeWidth={2.6} />
                  </button>
                )}
              </>
            ) : null}
            {err && <p className="mt-4 text-sm text-hv-coral" data-testid="home-error">{err}</p>}
          </section>

          {scope && (
            <ClassStories heading="This class's stories" started={!!scope.started} reached={reached} current={current}
                          note={scope.started ? null : "These open for the class once you press Start from Today."} />
          )}
        </>
      )}
      {showAttendance && scope && (
        <AttendanceModal scope={scope} onClose={() => setShowAttendance(false)} onSaved={() => load(scope.scope_id)} />
      )}
    </main>
  );
}
